import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import UserInfo from './UserInfo';
import {
  selectOrganizationUsersData,
  selectSelectedUserIdData,
} from '../../store/organization/organization-slice-selectors';
import {
  resetUsersRestState,
  setSelectedUserId,
} from '../../store/organization/organization-slice';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import MainContentBlock from '../ui/MainContentBlock';
import { useTranslation } from 'react-i18next';
import { NotificationIcon, UserIcon } from '@common/utils/icons/icons';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getUserById } from '../../store/organization/organization-actions';
import NotificationSettingsPage from '../notifications/user-configurations/NotificationSettingsPage';
import { selectUser } from '../../store/general/general-slice-selectors';
import { resetRobotsState } from '../../store/robots/robots-slice';
import { resetGeneralState } from '../../store/general/general-slice';
import { removeToken } from '../../services/data/axios-base';

export enum UserOverlayTabs {
  User = 'user',
  Notifications = 'notifications',
}

const UserOverlay: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [filterParams, setFilterParams] = useSearchParams();
  const queryUserId = filterParams.get(FilterKeys.UserId);
  const queryTab = filterParams.get(FilterKeys.Tab);

  const selectedUserId = useAppSelector((state) => selectSelectedUserIdData(state));
  const organizationUserData = useAppSelector((state) => selectOrganizationUsersData(state));
  const currentUserData = useAppSelector((state) => selectUser(state));

  const [isSliderOpen, setIsSliderOpen] = useState(queryUserId != null);

  const user = organizationUserData.current;

  useEffect(() => {
    setIsSliderOpen(queryUserId != null);
  }, [queryUserId]);

  useEffect(() => {
    if (
      isSliderOpen &&
      queryTab != null &&
      !Object.values(UserOverlayTabs).some((availableTab) => availableTab === queryTab)
    ) {
      setFilterParams((prevState) => {
        prevState.set(FilterKeys.Tab, UserOverlayTabs.User);
        return prevState;
      });
    }
  }, [isSliderOpen, queryTab, setFilterParams]);

  useEffect(() => {
    if (queryUserId !== selectedUserId) {
      dispatch(setSelectedUserId(queryUserId ?? null));
    }
  }, [dispatch, queryUserId, selectedUserId]);

  useEffect(() => {
    if (organizationUserData.rest.getByIdStatus === HttpFetchStatus.Idle && queryUserId != null) {
      dispatch(getUserById(queryUserId));
    }
  }, [dispatch, organizationUserData.rest.getByIdStatus, queryUserId]);

  const closeOverlayClickHandler = useCallback(() => {
    setFilterParams((prevState) => {
      prevState.delete(FilterKeys.UserId);
      prevState.delete(FilterKeys.Tab);
      return prevState;
    });
    setIsSliderOpen(false);
  }, [setFilterParams]);

  useEffect(() => {
    if (organizationUserData.rest.deleteStatus === HttpFetchStatus.Success) {
      if (currentUserData.data.id === user?.id) {
        dispatch(resetRobotsState());
        dispatch(resetGeneralState());
        removeToken();
      }
      closeOverlayClickHandler();
      dispatch(resetUsersRestState({ deleteStatus: HttpFetchStatus.Idle, deleteError: null }));
    }
  }, [
    closeOverlayClickHandler,
    currentUserData.data.id,
    dispatch,
    organizationUserData.rest.deleteStatus,
    user?.id,
  ]);

  const userTabClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.Tab, UserOverlayTabs.User);
      return prevState;
    });
  };

  const notificationsTabClickHandler = () => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.Tab, UserOverlayTabs.Notifications);
      return prevState;
    });
  };

  if (!isSliderOpen) return null;

  const pages = [
    {
      open: queryTab === UserOverlayTabs.User || queryTab == null,
      content: user == null ? null : <UserInfo />,
      tooltip: t('button-dashboard'),
      title: user?.name,
      icon: <UserIcon />,
      onClick: userTabClickHandler,
    },
  ];

  if (user?.id === currentUserData.data.id) {
    pages.push({
      open: queryTab === UserOverlayTabs.Notifications,
      content: <NotificationSettingsPage />,
      tooltip: t('button-notification-settings'),
      title: `${user?.name ?? ''} - ${t('button-notification-settings')}`,
      icon: <NotificationIcon />,
      onClick: notificationsTabClickHandler,
    });
  }

  return (
    <React.Fragment>
      <MainContentBlock isOverlayContent onClose={closeOverlayClickHandler} pages={pages} />
    </React.Fragment>
  );
};

export default UserOverlay;
